/**
 * The Equipment step's Rows view (`equipment-page-table`, R1–R6, R19–R20; frame 07).
 *
 * The register on the design system's list `Table`, over the same column set the Groups view draws
 * (`equipmentRegisterColumns`), now sortable, selectable and edited in the cell. It replaces step 2's
 * mount of `AssetRegisterList`, which admin keeps unchanged (NFR2). No pager: the table's own
 * scroller is bounded by the main panel, so the header stays in view while the rows scroll (R4).
 *
 * Above the table, the step's notices, rendered from the pieces `equipment-page-register-notices`
 * lifted out of the old list with their props unchanged (R19). Below it, the foot: *Add an Asset*
 * and *Remove all* (R20). What an upload did is said by the import summary above the views
 * (`EquipmentLandedSummary`), not here.
 *
 * It owns presentation and the cell's open state only. The page owns every read, the sort, the
 * selection and the dialogs, so nothing here can count or order a second way.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import styled from 'styled-components';
import { Banner, Button, Icon, Table } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import {
  RegisterDriftLines,
  RegisterWriteBanners,
  RemoveAllButton,
  visuallyHidden,
  IconDisc,
  rowArrivedLook,
  ROW_MOTION_KEY_ATTR,
  useRowMotion,
} from '@repo/ui';
import {
  IDENTITY_STATE_LABELS,
  registerRowName,
  sortRows,
  type DriftEntity,
  type IdentifyModel,
  type PlanRegisterDrift,
  type SortDirection,
} from '@repo/front-end-utils';
import type { TwinAssetResponse } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import { equipmentRegisterColumns, equipmentSortAccessors } from './equipmentRegisterColumns';
import { editNameOnF2, useRegisterCellEditor } from './useRegisterCellEditor';
import { nameIndexWithout } from './equipmentRowEdit';
import { SELECTION_BAR_CLEARANCE } from './EquipmentSelectionBar';
import EquipmentAddRow, { type AddAttempt } from './EquipmentAddRow';
import type { EquipmentWriteDeps } from './useEquipmentRowWrites';
import { fromImportOrder, type FromImport } from './equipmentShownOrder';

export interface EquipmentSort {
  field: string | null;
  direction: SortDirection;
}

/** The notices above the table — the old list's props, passed through unchanged (R19). */
export interface EquipmentRowsNotices {
  actionError?: string | null;
  actionErrorKey?: number;
  actionNotice?: string | null;
  /** The coverage index's count of plan items no register row backs. */
  orphanPlacementCount: number;
  drift?: PlanRegisterDrift | null;
  onAddMissingToRegister?: (entities: readonly DriftEntity[]) => void;
  addingProgress?: { done: number; total: number } | null;
}

export interface EquipmentRowsViewProps {
  /** The ONE identify model; `null` when the rows could not be counted (read-only rows then). */
  model: Pick<IdentifyModel, 'stateOf' | 'planStateOf'> | null;
  rows: readonly TwinAssetResponse[];
  /** The register-joined plan: where a placed row is, and the names the answers check against. */
  layout: FloorPlannerLayout | null | undefined;
  /** The rows the chip and the search allow; undefined shows every row. */
  visibleIds: ReadonlySet<number> | undefined;
  /** Where the register read stands. */
  read: 'loading' | 'error' | 'ready';
  /** What a failed read says. */
  readError: string;
  onRetryRead: () => void;
  sort: EquipmentSort;
  onSort: (field: string) => void;
  /** The page's one selection, shared with the Groups view (R11). */
  selectedKeys: readonly number[];
  onSelectionChange: (keys: (string | number)[]) => void;
  notices: EquipmentRowsNotices;
  /** Whether placement is known — gates *Remove all* (the coverage index's `planKnown`). */
  planKnown: boolean;
  onRemoveAll?: () => void;
  /**
   * A row click, or Enter on a focused row: opens the asset card (`equipment-page-asset-card`, R1).
   * With a card open, the arrow keys call it for the row they land on. Latest wins.
   */
  onOpenRow: (row: TwinAssetResponse) => void;
  /**
   * The row the asset card is open on, or null (R2): the table draws it active, narrows to Name,
   * Product and Plan, and marks its name.
   */
  openId?: number | null;
  /** The table's writes; absent, the cells are read-only. */
  writes?: EquipmentWriteDeps;
  /**
   * *From this import* (`equipment-page-reimport`, R11): while set, the table shows the import's
   * rows, added first, in place of the sort, and the added rows glow as they arrive.
   */
  fromImport?: FromImport | null;
}

/** A minimum the table keeps, so tall notices scroll the panel rather than crushing the rows. */
const TABLE_MIN_HEIGHT = `calc(${space[12]} * 7)`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  gap: ${space[3]};

  > .rows-table {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: ${TABLE_MIN_HEIGHT};
  }

  /* A row an upload added (R11): the list row look's arrived glow, in mint, fading out of it. */
  > .rows-table [role='row'] {
    ${rowArrivedLook}
  }

  /* The selection bar is overlaid on the foot's middle, so the foot keeps its height whether or
     not the bar shows: appearing then moves nothing (R13, NFR1). */
  > .rows-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[3]};
    min-height: ${SELECTION_BAR_CLEARANCE};
  }
`;

export const EMPTY_REGISTER_MESSAGE =
  'No assets on the register yet. Upload a CSV, or add one by hand.';
export const NO_MATCH_MESSAGE = 'No assets match';
/** An empty register that is not the start screen offers no add row, so it does not suggest one. */
export const EMPTY_REGISTER_NO_ADD_MESSAGE = 'No assets on the register yet.';

/** Read-only rows when the model could not be built: no state, no plan, so nothing to gate on. */
const UNCOUNTED_MODEL: Pick<IdentifyModel, 'stateOf' | 'planStateOf'> = {
  stateOf: () => undefined,
  planStateOf: () => undefined,
};

/**
 * Tab from the open row enters the asset card (`equipment-page-asset-card`, Block A). The card docks
 * after the table's own controls (*Add an Asset*, *Remove all*), so without this Tab would reach those
 * first. Shift+Tab, and Tab from anywhere else, keep the page's order.
 */
export const tabIntoAssetCard = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'Tab' || event.shiftKey || event.defaultPrevented) return;
  const target = event.target as HTMLElement;
  if (target.getAttribute('aria-current') !== 'true' || target.getAttribute('role') !== 'row')
    return;
  const first = document
    .querySelector('.equipment-asset-card')
    ?.querySelector<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled])',
    );
  if (!first) return;
  event.preventDefault();
  first.focus();
};

interface OpenRowStore {
  get: () => number | null;
  set: (id: number | null) => void;
  subscribe: (listener: () => void) => () => void;
}

const createOpenRowStore = (): OpenRowStore => {
  let current: number | null = null;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set: id => {
      if (id === current) return;
      current = id;
      // forEach, never for…of: this app compiles to ES5 without downlevelIteration.
      listeners.forEach(listener => listener());
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/** Frame 08's cyan disc beside the open row's name; nothing on any other row. Decorative. */
const OpenRowMark: React.FC<{ rowId: number; store: OpenRowStore }> = ({ rowId, store }) => {
  const open = useSyncExternalStore(store.subscribe, () => store.get() === rowId);
  return open ? (
    <IconDisc tone="selected" size={20} flat className="open-disc">
      <Icon name="pen" size="xs" />
    </IconDisc>
  ) : null;
};

export const EquipmentRowsView: React.FC<EquipmentRowsViewProps> = ({
  model,
  rows,
  layout,
  visibleIds,
  read,
  readError,
  onRetryRead,
  sort,
  onSort,
  selectedKeys,
  onSelectionChange,
  notices,
  planKnown,
  onRemoveAll,
  onOpenRow,
  openId = null,
  writes,
  fromImport = null,
}) => {
  const counted = model !== null;
  // Edited in the cell only on rows the model could read: every answer and gate needs it.
  const { editCell, add } = useRegisterCellEditor({
    rows,
    layout,
    writes: counted ? writes : undefined,
  });
  const columnModel = model ?? UNCOUNTED_MODEL;

  const shown = useMemo(() => {
    // *From this import* (R11): the import's rows in its order, the sort skipped while it stands.
    // The same function `equipmentShownOrder` calls, so the selection's order agrees.
    if (fromImport) {
      const byId = new Map(rows.map(row => [row.id, row]));
      return fromImportOrder(rows, visibleIds, fromImport)
        .map(id => byId.get(id))
        .filter((row): row is TwinAssetResponse => row !== undefined);
    }
    const visible = visibleIds ? rows.filter(row => visibleIds.has(row.id)) : [...rows];
    return sortRows(visible, sort.field, sort.direction, equipmentSortAccessors(columnModel));
  }, [rows, visibleIds, sort.field, sort.direction, columnModel, fromImport]);

  /*
   * The arrival glow (R11): every shown row in one group, so nothing ever "moves" — rows only
   * arrive. An added row glows when the re-read first brings it, or on mount. The epoch is the sort
   * alone: landing on *From this import* must not change it, since an epoch change clears a glow.
   */
  const tableRef = useRef<HTMLDivElement>(null);
  const motionGroups = useMemo(
    () => new Map(shown.map(row => [String(row.id), 'rows'] as const)),
    [shown],
  );
  const arrived = useMemo(
    () => (fromImport ? new Set(Array.from(fromImport.added, String)) : undefined),
    [fromImport],
  );
  useRowMotion(tableRef, {
    groups: motionGroups,
    epoch: `${sort.field ?? ''}:${sort.direction}`,
    arrived,
  });

  // *Add an Asset* (R17): offered only on counted rows with somewhere to write.
  const [adding, setAdding] = useState(false);
  // The add row's attempt, held here so a failed re-read (which hides the add row) cannot lose an
  // unresolved attempt's key; a new twin starts with none.
  const addAttempt = useRef<AddAttempt | null>(null);
  const writesTwin = writes?.twinId ?? null;
  useEffect(() => {
    addAttempt.current = null;
  }, [writesTwin]);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [pendingAdd, setPendingAdd] = useState<{ id: number; name: string } | null>(null);
  const [addAnnounced, setAddAnnounced] = useState('');
  const newcomerIndex = useMemo(
    () => (adding ? nameIndexWithout(layout, rows, null) : null),
    [adding, layout, rows],
  );

  /*
   * The open row, as a tiny store the Name cells' marks subscribe to: moving the open row re-renders
   * the two marks that change, not every row's cells (the table memoises rows on `columns`, and an
   * arrow key over every row is the cost it was built to avoid). The columns change only when the
   * card opens or closes, which narrows the table.
   */
  const [openStore] = useState(() => createOpenRowStore());
  useLayoutEffect(() => openStore.set(openId), [openStore, openId]);
  const openMark = useCallback(
    (rowId: number) => <OpenRowMark rowId={rowId} store={openStore} />,
    [openStore],
  );
  const cardOpen = openId !== null;
  // The row the add row just made, as the one-entry set the columns mark as added (R6).
  const addedIds = useMemo(() => (addedId === null ? undefined : new Set([addedId])), [addedId]);
  const columns = useMemo(
    () =>
      equipmentRegisterColumns(columnModel, {
        narrow: cardOpen,
        sortable: true,
        editable: counted && editCell !== undefined,
        editCell,
        addedIds,
        openMark,
      }),
    [columnModel, counted, editCell, addedIds, cardOpen, openMark],
  );

  const rootRef = useRef<HTMLDivElement>(null);

  /*
   * Once the new row is on the register: say so — and where it is, when the chip and search hide it
   * (a new row is *To identify*) — and bring it into view.
   */
  useEffect(() => {
    if (!pendingAdd) return;
    const created = rows.find(r => r.id === pendingAdd.id);
    if (!created) return;
    setPendingAdd(null);
    const hidden = visibleIds !== undefined && !visibleIds.has(created.id);
    const state = model?.stateOf(created.id);
    setAddAnnounced(
      hidden
        ? `Added ${pendingAdd.name}; it is under ${state ? IDENTITY_STATE_LABELS[state] : 'another filter'}`
        : `Added ${pendingAdd.name}`,
    );
    if (!hidden) {
      window.setTimeout(() => {
        const rowEl = Array.from(
          rootRef.current?.querySelectorAll<HTMLElement>('[data-edit-field="name"]') ?? [],
        ).find(el => el.getAttribute('aria-label') === `Rename ${pendingAdd.name}`);
        rowEl?.closest('[role="row"]')?.scrollIntoView?.({ block: 'nearest' });
      }, 0);
    }
  }, [pendingAdd, rows, visibleIds, model]);

  const settled = read === 'ready';

  return (
    <Root ref={rootRef} data-testid="equipment-rows-view">
      <RegisterWriteBanners
        actionError={notices.actionError}
        actionErrorKey={notices.actionErrorKey}
        actionNotice={notices.actionNotice}
      />
      {/* Claims about the plan and the register wait for the read to settle, as the list's did. */}
      {settled && (
        <RegisterDriftLines
          orphanPlacementCount={notices.orphanPlacementCount}
          drift={notices.drift}
          onAddMissingToRegister={notices.onAddMissingToRegister}
          addingProgress={notices.addingProgress}
        />
      )}
      {read === 'error' ? (
        <Banner surface="on-glass" tone="error">
          {readError} {/* An explicit name: a write refusal can sit above at the same time. */}
          <Button
            variant="ghost"
            size="xs"
            onClick={onRetryRead}
            aria-label="Try reading the register again"
          >
            Try again
          </Button>
        </Banner>
      ) : (
        <>
          <div
            ref={tableRef}
            className="rows-table"
            onKeyDown={event => {
              editNameOnF2(event);
              tabIntoAssetCard(event);
            }}
          >
            <Table<TwinAssetResponse>
              look="list"
              selectable={counted}
              columns={columns}
              data={shown}
              keyExtractor={row => row.id}
              rowKeyAttribute={ROW_MOTION_KEY_ATTR}
              getRowLabel={registerRowName}
              selectedKeys={selectedKeys as number[]}
              onSelectionChange={onSelectionChange}
              sortField={sort.field ?? undefined}
              sortDirection={sort.direction}
              onSort={onSort}
              onRowClick={row => onOpenRow(row)}
              activeKey={openId}
              loading={read === 'loading' && rows.length === 0}
              emptyMessage={
                rows.length === 0
                  ? add
                    ? EMPTY_REGISTER_MESSAGE
                    : EMPTY_REGISTER_NO_ADD_MESSAGE
                  : NO_MATCH_MESSAGE
              }
              aria-label="Equipment register"
            />
          </div>
          {adding && add && newcomerIndex && (
            <EquipmentAddRow
              attemptRef={addAttempt}
              nameIndex={newcomerIndex}
              onAdd={(answer, key) => {
                setAddedId(null);
                return add(answer, key);
              }}
              onAdded={created => {
                setAddedId(created.id);
                setPendingAdd({ id: created.id, name: registerRowName(created) });
              }}
              onClose={cancelled => {
                setAdding(false);
                // Escape hands focus back to the control that opened the field (A2).
                if (cancelled) {
                  window.setTimeout(
                    () =>
                      rootRef.current?.querySelector<HTMLElement>('[data-register-add]')?.focus(),
                    0,
                  );
                }
              }}
            />
          )}
          <span role="status" aria-live="polite" style={visuallyHidden}>
            {addAnnounced}
          </span>
          {(add || (onRemoveAll && rows.length > 0)) && (
            <div className="rows-foot">
              {add ? (
                <Button
                  variant="text"
                  tone="brand"
                  size="sm"
                  iconLeft={<Icon name="plus" size="sm" />}
                  aria-expanded={adding}
                  onClick={() => {
                    setAddAnnounced('');
                    setAdding(true);
                  }}
                  data-register-add=""
                >
                  Add an Asset
                </Button>
              ) : (
                <span />
              )}
              {onRemoveAll && rows.length > 0 && (
                <RemoveAllButton planKnown={planKnown} onClick={onRemoveAll} />
              )}
            </div>
          )}
        </>
      )}
    </Root>
  );
};

export default EquipmentRowsView;
